<template>
  <div class="w-full h-full flex items-center justify-center">
    <canvas ref="chartCanvas" class="w-full h-full max-w-full max-h-full"></canvas>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick, defineProps } from 'vue'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  LineElement,
  LineController,
  PointElement,
  ArcElement,
  DoughnutController,
  PieController,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'

// Registrar componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  LineElement,
  LineController,
  PointElement,
  ArcElement,
  DoughnutController,
  PieController,
  Title,
  Tooltip,
  Legend,
  Filler
)

const props = defineProps({
  type: { type: String, default: 'bar' },
  labels: { type: Array, default: () => [] },
  data: { type: Array, default: () => [] },
  colors: { type: Array, default: () => ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316'] },
  options: { type: Object, default: () => ({}) },
  responsive: { type: Boolean, default: true },
  maintainAspectRatio: { type: Boolean, default: false }
})

const chartCanvas = ref(null)
let chartInstance = null

// Função para formatar valores
const formatValue = (value) => {
  if (typeof value === 'number') {
    if (value >= 1000000) {
      return (value / 1000000).toFixed(1) + 'M'
    } else if (value >= 1000) {
      return (value / 1000).toFixed(1) + 'K'
    }
    return value.toLocaleString('pt-BR')
  }
  return value || '0'
}

// Função para detectar se é dispositivo móvel
const isMobile = () => {
  return window.innerWidth <= 768
}

// Configurações padrão para diferentes tipos de gráfico
const getChartConfig = () => {
  const mobile = isMobile()
  
  const baseConfig = {
    responsive: props.responsive,
    maintainAspectRatio: props.maintainAspectRatio,
    interaction: {
      intersect: false,
      mode: 'index'
    },
    plugins: {
      legend: {
        display: props.type === 'pie' || props.type === 'doughnut',
        position: mobile ? 'bottom' : 'bottom',
        labels: {
          padding: mobile ? 10 : 15,
          usePointStyle: true,
          font: {
            size: mobile ? 10 : 12
          },
          boxWidth: mobile ? 12 : 15,
          boxHeight: mobile ? 12 : 15
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        titleFont: {
          size: mobile ? 12 : 14
        },
        bodyFont: {
          size: mobile ? 11 : 13
        },
        padding: mobile ? 8 : 12,
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = formatValue(context.parsed.y || context.parsed);
            return `${label}: ${value}`;
          }
        }
      }
    },
    ...props.options
  }

  // Configurações específicas por tipo
  if (props.type === 'bar') {
    baseConfig.scales = {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
          lineWidth: mobile ? 0.5 : 1
        },
        ticks: {
          font: {
            size: mobile ? 10 : 12
          },
          maxTicksLimit: mobile ? 5 : 8,
          callback: function(value) {
            return formatValue(value);
          }
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: mobile ? 9 : 11
          },
          maxRotation: mobile ? 45 : 0,
          minRotation: mobile ? 0 : 0
        }
      }
    }
  } else if (props.type === 'line' || props.type === 'area') {
    baseConfig.scales = {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
          lineWidth: mobile ? 0.5 : 1
        },
        ticks: {
          font: {
            size: mobile ? 10 : 12
          },
          maxTicksLimit: mobile ? 5 : 8,
          callback: function(value) {
            return formatValue(value);
          }
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: mobile ? 9 : 11
          },
          maxRotation: mobile ? 45 : 0,
          minRotation: mobile ? 0 : 0
        }
      }
    }
    
    if (props.type === 'area') {
      baseConfig.elements = {
        line: {
          fill: true
        }
      }
    }
  }

  return baseConfig
}

// Função para obter dados do gráfico
const getChartData = () => {
  const mobile = isMobile()
  
  if (props.type === 'pie' || props.type === 'doughnut') {
    return {
      labels: props.labels,
      datasets: [{
        data: props.data,
        backgroundColor: props.colors,
        borderWidth: mobile ? 1 : 2,
        borderColor: '#ffffff',
        hoverBorderWidth: mobile ? 2 : 3,
        hoverBorderColor: '#ffffff'
      }]
    }
  } else {
    return {
      labels: props.labels,
      datasets: [{
        label: 'Dados',
        data: props.data,
        backgroundColor: props.type === 'area' 
          ? props.colors.map(color => color + '40') 
          : props.colors,
        borderColor: props.colors[0],
        borderWidth: props.type === 'line' || props.type === 'area' ? (mobile ? 2 : 3) : 1,
        fill: props.type === 'area',
        tension: props.type === 'line' || props.type === 'area' ? 0.4 : 0,
        pointBackgroundColor: props.colors[0],
        pointBorderColor: '#ffffff',
        pointBorderWidth: mobile ? 1 : 2,
        pointRadius: props.type === 'line' || props.type === 'area' ? (mobile ? 3 : 4) : 0,
        pointHoverRadius: props.type === 'line' || props.type === 'area' ? (mobile ? 5 : 6) : 0
      }]
    }
  }
}

// Função para criar o gráfico
const createChart = async () => {
  if (!chartCanvas.value || !props.data.length) return

  await nextTick()

  // Destruir gráfico existente
  if (chartInstance) {
    chartInstance.destroy()
    chartInstance = null
  }

  try {
    const ctx = chartCanvas.value.getContext('2d')
    
    // Configurar o canvas para alta resolução em dispositivos móveis
    const devicePixelRatio = window.devicePixelRatio || 1
    const rect = chartCanvas.value.getBoundingClientRect()
    
    chartCanvas.value.width = rect.width * devicePixelRatio
    chartCanvas.value.height = rect.height * devicePixelRatio
    
    ctx.scale(devicePixelRatio, devicePixelRatio)
    
    chartCanvas.value.style.width = rect.width + 'px'
    chartCanvas.value.style.height = rect.height + 'px'
    
    chartInstance = new ChartJS(ctx, {
      type: props.type === 'area' ? 'line' : props.type,
      data: getChartData(),
      options: getChartConfig()
    })
  } catch (error) {
    console.error('Erro ao criar gráfico:', error)
  }
}

// Função para atualizar o gráfico
const updateChart = () => {
  if (!chartInstance) {
    createChart()
    return
  }

  try {
    chartInstance.data = getChartData()
    chartInstance.options = getChartConfig()
    chartInstance.update('active')
  } catch (error) {
    console.error('Erro ao atualizar gráfico:', error)
    createChart() // Recriar em caso de erro
  }
}

// Watchers para reagir a mudanças nas props
watch([() => props.type, () => props.data, () => props.labels, () => props.colors], () => {
  updateChart()
}, { deep: true })

watch(() => props.options, () => {
  updateChart()
}, { deep: true })

// Lifecycle hooks
onMounted(() => {
  createChart()
})

onUnmounted(() => {
  if (chartInstance) {
    chartInstance.destroy()
    chartInstance = null
  }
})
</script>

<style scoped>
canvas {
  max-width: 100% !important;
  max-height: 100% !important;
}
</style>